import dateService from "../../../../../../common/utils/date/date.service";
import TableLinkButtonComponent from "../../../../../../common/components/table/button/link/table-link-button.component";
import { faCircleInfo, faEdit } from "@fortawesome/free-solid-svg-icons";
import billingRoutesHelper from "../../../../common/routes/billing-routes.helper";
import BillingsTaxiRouteListingTableColumn from "../types/billings-taxi-route-listing-table-column";
import billingsTranslationsHelper from "../../../../../../languages/billings-translations.helper";
import BillingsTaxiRouteListingItem from "../types/billings-taxi-route-listing-item";
import BillingsTaxiRouteListingTableRow from "../types/billings-taxi-route-listing-table-row";

const getColumns = (): BillingsTaxiRouteListingTableColumn[] => {
  const translations =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().table
      .headers;

  return [
    {
      accessor: "routeStartDate",
      header: translations.routeStartDateLabel,
      title: translations.routeStartDateTitle,
      colSpan: 10,
    },
    {
      accessor: "routeId",
      header: translations.routeIdLabel,
      title: translations.routeIdTitle,
      colSpan: 4,
    },
    {
      accessor: "cargoOrderInternalIds",
      header: translations.cargoOrderInternalIdsLabel,
      title: translations.cargoOrderInternalIdsTitle,
      colSpan: 10,
    },
    {
      accessor: "driver",
      header: translations.driverLabel,
      title: translations.driverTitle,
      colSpan: 10,
    },
    {
      accessor: "fleetPartner",
      header: translations.fleetPartnerLabel,
      title: translations.fleetPartnerTitle,
      colSpan: 10,
    },
    {
      accessor: "addresses",
      header: translations.routeLabel,
      title: translations.routeTitle,
      colSpan: 12,
    },
    {
      accessor: "billingModel",
      header: translations.billingModelLabel,
      title: translations.billingModelTitle,
      colSpan: 6,
    },
    {
      accessor: "distance",
      header: translations.distanceLabel,
      title: translations.distanceTitle,
      colSpan: 10,
    },
    {
      accessor: "additionalDistance",
      header: translations.additionalDistanceLabel,
      title: translations.additionalDistanceTitle,
      colSpan: 10,
    },
    {
      accessor: "baseDistanceRate",
      header: translations.baseDistanceRateLabel,
      title: translations.baseDistanceRateTitle,
      colSpan: 10,
    },
    {
      accessor: "rateForDistance",
      header: translations.rateForDistanceLabel,
      title: translations.rateForDistanceTitle,
      colSpan: 10,
    },
    {
      accessor: "amountForDistance",
      header: translations.amountForDistanceLabel,
      title: translations.amountForDistanceTitle,
      colSpan: 10,
    },
    {
      accessor: "stopCost",
      header: translations.stopoverCostLabel,
      title: translations.stopoverCostTitle,
      colSpan: 9,
    },
    {
      accessor: "tollRoadsCost",
      header: translations.tollRoadsCostLabel,
      title: translations.tollRoadsCostTitle,
      colSpan: 10,
    },
    {
      accessor: "sumOfBonuses",
      header: translations.sumOfBonusesLabel,
      title: translations.sumOfBonusesTitle,
      colSpan: 9,
    },
    {
      accessor: "sumOfPenalties",
      header: translations.sumOfPenaltiesLabel,
      title: translations.sumOfPenaltiesTitle,
      colSpan: 9,
    },
    {
      accessor: "totalCost",
      header: translations.totalCostLabel,
      title: translations.totalCostTitle,
      colSpan: 10,
    },
    {
      accessor: "actions",
      header: "",
      title: "",
      colSpan: 6,
    },
  ];
};

const getDistanceLabel = (
  distance: BillingsTaxiRouteListingItem["distance"]
): string => {
  return `${distance} km`;
};

const getDistanceTitle = (
  distance: BillingsTaxiRouteListingItem["distance"]
): string => {
  return `${distance} km`;
};

const getAdditionalDistanceLabel = (
  additionalDistance: BillingsTaxiRouteListingItem["additionalDistance"]
): string => {
  return `${additionalDistance} km`;
};

const getAdditionalDistanceTitle = (
  additionalDistance: BillingsTaxiRouteListingItem["additionalDistance"]
): string => {
  return `${additionalDistance} km`;
};

const getBaseDistanceRateLabel = (
  baseDistanceRate: BillingsTaxiRouteListingItem["baseDistanceRate"]
): string => {
  return `${String(baseDistanceRate.toFixed(2))} PLN/km`;
};

const getBaseDistanceRateTitle = (
  baseDistanceRate: BillingsTaxiRouteListingItem["baseDistanceRate"]
): string => {
  return `${String(baseDistanceRate.toFixed(2))} PLN/km`;
};

const getRateForDistanceLabel = (
  rateForDistance: BillingsTaxiRouteListingItem["rateForDistance"]
): string => {
  if (rateForDistance === undefined) {
    return "0";
  }

  return `${String(rateForDistance.toFixed(2))} PLN/km`;
};

const getRateForDistanceTitle = (
  rateForDistance: BillingsTaxiRouteListingItem["rateForDistance"]
): string => {
  if (rateForDistance === undefined) {
    return "0";
  }

  return `${String(rateForDistance.toFixed(2))} PLN/km`;
};

const getStopoverCostLabel = (
  stopoverCost: BillingsTaxiRouteListingItem["stopoverCost"]
): string => {
  return `${String(stopoverCost.toFixed(2))} PLN`;
};

const getStopoverCostTitle = (
  stopoverCost: BillingsTaxiRouteListingItem["stopoverCost"]
): string => {
  return `${String(stopoverCost.toFixed(2))} PLN`;
};

const getTollRoadsCostLabel = (
  tollRoadsCost: BillingsTaxiRouteListingItem["tollRoadsCost"]
): string => {
  return `${String(tollRoadsCost.toFixed(2))} PLN`;
};

const getTollRoadsCostTitle = (
  tollRoadsCost: BillingsTaxiRouteListingItem["tollRoadsCost"]
): string => {
  return `${String(tollRoadsCost.toFixed(2))} PLN`;
};

const getAmountForDistanceLabel = (
  amountForDistance: BillingsTaxiRouteListingItem["amountForDistance"]
): string => {
  return `${String(amountForDistance.toFixed(2))} PLN`;
};

const getAmountForDistanceTitle = (
  amountForDistance: BillingsTaxiRouteListingItem["amountForDistance"]
): string => {
  return `${String(amountForDistance.toFixed(2))} PLN`;
};

const getTotalCostLabel = (
  totalCost: BillingsTaxiRouteListingItem["totalCost"]
): string => {
  return `${String(totalCost.toFixed(2))} PLN`;
};

const getTotalCostTitle = (
  totalCost: BillingsTaxiRouteListingItem["totalCost"]
): string => {
  return `${String(totalCost.toFixed(2))} PLN`;
};

const getSumOfBonusesLabel = (
  sumOfBonuses: BillingsTaxiRouteListingItem["sumOfBonuses"]
): string => {
  const template =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().table
      .sumOfBonusesLabelTemplateText;

  return template.replace("#{cost}", String(sumOfBonuses));
};

const getSumOfBonusesTitle = (
  sumOfBonuses: BillingsTaxiRouteListingItem["sumOfBonuses"]
): string => {
  const template =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().table
      .sumOfBonusesTitleTemplateText;

  return template.replace("#{cost}", String(sumOfBonuses));
};

const getSumOfPenaltiesLabel = (
  sumOfPenalties: BillingsTaxiRouteListingItem["sumOfPenalties"]
): string => {
  const template =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().table
      .sumOfPenaltiesLabelTemplateText;

  return template.replace("#{cost}", String(sumOfPenalties));
};

const getSumOfPenaltiesTitle = (
  sumOfPenalties: BillingsTaxiRouteListingItem["sumOfPenalties"]
): string => {
  const template =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().table
      .sumOfPenaltiesTitleTemplateText;

  return template.replace("#{cost}", String(sumOfPenalties));
};

const getRow = (
  listingItem: BillingsTaxiRouteListingItem
): BillingsTaxiRouteListingTableRow => {
  const translations =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().table;

  const startDateLabel = dateService.format(
    listingItem.routeStartDate,
    "dd.mm.yyyy"
  );

  const startDateTitle = dateService.format(
    listingItem.routeStartDate,
    "dd.mm.yyyy"
  );

  const distanceLabel = getDistanceLabel(listingItem.distance);
  const distanceTitle = getDistanceTitle(listingItem.distance);

  const additionalDistanceLabel = getAdditionalDistanceLabel(
    listingItem.additionalDistance
  );
  const additionalDistanceTitle = getAdditionalDistanceTitle(
    listingItem.additionalDistance
  );

  const baseDistanceRateLabel = getBaseDistanceRateLabel(
    listingItem.baseDistanceRate
  );
  const baseDistanceRateTitle = getBaseDistanceRateTitle(
    listingItem.baseDistanceRate
  );

  const rateForDistanceLabel = getRateForDistanceLabel(
    listingItem.rateForDistance
  );
  const rateForDistanceTitle = getRateForDistanceTitle(
    listingItem.rateForDistance
  );

  const stopoverCostLabel = getStopoverCostLabel(listingItem.stopoverCost);
  const stopoverCostTitle = getStopoverCostTitle(listingItem.stopoverCost);

  const tollRoadsCostLabel = getTollRoadsCostLabel(listingItem.tollRoadsCost);
  const tollRoadsCostTitle = getTollRoadsCostTitle(listingItem.tollRoadsCost);

  const amountForDistanceLabel = getAmountForDistanceLabel(
    listingItem.amountForDistance
  );
  const amountForDistanceTitle = getAmountForDistanceTitle(
    listingItem.amountForDistance
  );

  const totalCostLabel = getTotalCostLabel(listingItem.totalCost);
  const totalCostTitle = getTotalCostTitle(listingItem.totalCost);

  const sumOfBonusesLabel = getSumOfBonusesLabel(listingItem.sumOfBonuses);
  const sumOfBonusesTitle = getSumOfBonusesTitle(listingItem.sumOfBonuses);

  const sumOfPenaltiesLabel = getSumOfPenaltiesLabel(
    listingItem.sumOfPenalties
  );
  const sumOfPenaltiesTitle = getSumOfPenaltiesTitle(
    listingItem.sumOfPenalties
  );

  return {
    id: listingItem.uuid,
    value: {
      routeStartDate: <div title={startDateLabel}>{startDateTitle}</div>,
      routeId: (
        <div title={String(listingItem.routeId)}>{listingItem.routeId}</div>
      ),
      cargoOrderInternalIds: (
        <div title={String(listingItem.cargoOrderInternalIds)}>
          {listingItem.cargoOrderInternalIds}
        </div>
      ),
      driver: (
        <div title={listingItem.driverName}>{listingItem.driverName}</div>
      ),
      fleetPartner: (
        <div title={listingItem.fleetPartner ?? ""}>
          {listingItem.fleetPartner}
        </div>
      ),
      billingModel: (
        <div title={listingItem.billingModel}>{listingItem.billingModel}</div>
      ),
      sumOfBonuses: <div title={sumOfBonusesTitle}>{sumOfBonusesLabel}</div>,
      sumOfPenalties: (
        <div title={sumOfPenaltiesTitle}>{sumOfPenaltiesLabel}</div>
      ),
      distance: <div title={distanceTitle}>{distanceLabel}</div>,
      additionalDistance: (
        <div title={additionalDistanceTitle}>{additionalDistanceLabel}</div>
      ),
      baseDistanceRate: (
        <div title={baseDistanceRateTitle}>{baseDistanceRateLabel}</div>
      ),
      rateForDistance: (
        <div title={rateForDistanceTitle}>{rateForDistanceLabel}</div>
      ),
      amountForDistance: (
        <div title={amountForDistanceTitle}>{amountForDistanceLabel}</div>
      ),
      addresses: (
        <div title={listingItem.addresses}>{listingItem.addresses}</div>
      ),
      stopCost: <div title={stopoverCostTitle}>{stopoverCostLabel}</div>,
      tollRoadsCost: <div title={tollRoadsCostTitle}>{tollRoadsCostLabel}</div>,
      totalCost: <div title={totalCostTitle}>{totalCostLabel}</div>,
      actions: (
        <div className="d-flex">
          <TableLinkButtonComponent
            icon={faEdit}
            to={billingRoutesHelper.getTaxiWithDriverEditRoute({
              billingUuid: listingItem.uuid,
            })}
            title={translations.taxiWithDriverBillingEditButtonTitle}
            idForTesting={`billings-taxi-route-listing-table-item-${listingItem.uuid}-edit-button`}
          />
          <TableLinkButtonComponent
            icon={faCircleInfo}
            to={billingRoutesHelper.getTaxiWithDriverDetailsRoute({
              billingUuid: listingItem.uuid,
            })}
            title={translations.taxiWithDriverBillingViewButtonTitle}
            idForTesting={`billings-taxi-route-listing-table-item-${listingItem.uuid}-details-button`}
          />
        </div>
      ),
    },
  };
};

const getRows = (
  listingItems: BillingsTaxiRouteListingItem[]
): BillingsTaxiRouteListingTableRow[] => {
  return listingItems.map(getRow);
};

const billingsTaxiRouteListingTableHelper = {
  getColumns,
  getRows,
};

export default billingsTaxiRouteListingTableHelper;
